import { type Key, useEffect } from 'react'
import SeeAll from '../SeeAll'

import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  useMediaQuery,
  Grid
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { useGetListingsMutation } from '../../redux/api/ihfApi'
import { useSelector } from 'react-redux'
import { type IListing } from '../../redux/api/types'

const ThinTableCell = styled(TableCell)(({ theme }) => ({
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 0,
  paddingRight: 0
}))

const HeaderCell = styled(TableCell)(({ theme }) => ({
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 0,
  paddingRight: 0,
  color: '#7D8FB3'
}))

function getListingsMethod () {
  const [getListings] = useGetListingsMutation()
  useEffect(() => {
    getListings({})
      .then(() => {})
      .catch(() => {})
  }, [])

  const ihfState = useSelector((state: any) => state.ihfState)
  const { activeListings } = ihfState
  return activeListings
}

const ActiveListingsTable: React.FC = () => {
  const listings = getListingsMethod()
  return (
    <TableContainer>
        <Table sx={{ tableLayout: 'fixed' }} aria-label="Monthly Activity">
          <TableHead>
            <TableRow sx={{ '& td, & th': { border: 0, paddingBottom: '10px' } }}>
              <HeaderCell sx={{ width: '51px' }}></HeaderCell>
              <HeaderCell sx={{ padding: '0 60px 0 30px', width: '100%' }}>Address</HeaderCell>
              <HeaderCell sx={{ width: '94px' }}>Price</HeaderCell>
              <HeaderCell sx={{ width: '95px' }}>MLS info</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listings?.map((item: IListing, index: Key | null | undefined) => {
              const { smallImageUrl, address, price, listingNumber } = item
              return (
                <TableRow
                  key={index}
                  sx={{ '& td, & th': { border: 0 } }}
                >
                  <ThinTableCell sx={{ width: '51px' }}>
                    <Box component="div"
                      sx={{
                        borderRadius: '10px',
                        height: '51px',
                        overflow: 'hidden',
                        width: '51px'
                      }}
                    >
                      <Box
                        component="img"
                        src={smallImageUrl}
                        sx={{
                          height: '100%',
                          objectFit: 'cover',
                          width: '100%'
                        }}
                      />
                    </Box>
                  </ThinTableCell>
                  <ThinTableCell
                    sx={{ padding: '0 60px 0 30px' }}
                  >
                    {address}
                  </ThinTableCell>
                  <ThinTableCell>${(price).toLocaleString('en-EN')}</ThinTableCell>
                  <ThinTableCell>{listingNumber}</ThinTableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
  )
}

const ActiveListingsGrid: React.FC = () => {
  const listings = getListingsMethod()
  return (
    <Grid container
      spacing={1}
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        overflowX: 'scroll',
        gap: '20px',
        maxHeight: 510
      }}
    >
      {listings?.map((item: IListing, index: Key | null | undefined) => {
        const { largeImageUrl, address, price, listingNumber } = item
        return (
          <Grid item key={index}
            sx={{
              p: 0,
              display: 'flex',
              gap: '30px',
              flexDirection: 'column',
              overflowY: 'hidden',
              overflowX: 'scroll',
              maxHeight: 510
            }}>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
                width: '100%',
                mt: 3
              }}
            >
              <Box
                component="img"
                src={largeImageUrl}
                sx={{
                  borderRadius: '10px',
                  height: '174px',
                  objectFit: 'cover',
                  width: '161px'
                }}
              />
              <Box sx={{
                ml: 2,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ mt: 0 }}>Address</Box>
                <Typography>{address}</Typography>
                <Box sx={{ mt: '5px' }}>Price</Box>
                <Typography>${(price).toLocaleString('en-EN')}</Typography>
                <Box sx={{ mt: '5px' }}>MLS info</Box>
                <Typography>{listingNumber}</Typography>
              </Box>
            </Box>
          </Grid>
        )
      })}
    </Grid>
  )
}

interface IActiveListings {
  openIHFDashboard: () => void
  isLoadingIHFLink: boolean
}

const ActiveListings = ({ openIHFDashboard, isLoadingIHFLink }: IActiveListings) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  return (
    <Paper sx={{ padding: '17px 24px 32px', boxShadow: 0, pb: '32px' }}>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: isMobile ? 0 : 2, height: '30px' }}>
        <Typography
          sx={{ color: theme.palette.grey[40] }}
          variant='caption'
        >
          Active Listings</Typography>
        <SeeAll onClick={openIHFDashboard} isDisable={isLoadingIHFLink} />
      </Box>
      { isMobile
        ? <ActiveListingsGrid />
        : <ActiveListingsTable />
      }
    </Paper>
  )
}

export default ActiveListings
